
/**
 * sold-out-inquiry.js
 *
 * - Opens/closes the sold-out inquiry modal
 * - Moves modal to body to avoid stacking/z-index issues
 * - Allows normal Shopify contact submission for CAPTCHA
 * - Saves and restores form drafts after redirects
 * - Recognizes Shopify's successful submission response
 * - Recovers confirmed mobile homepage redirects
 * - Resets the form when reopened after success
 * - Cleans up Swym wishlist injections
 *
 * Independent from product-inquiry.js and zero-price-contact.js
 */

(function () {
  const DRAFT_KEY = "soldOutInquiryDraft";
  const PENDING_KEY = "soldOutInquiryPending";
  const RETURN_URL_KEY = "soldOutInquiryReturnUrl";
  const MODAL_ID_KEY = "soldOutInquiryModalId";

  /*
   * Recover successful inquiries that Shopify
   * unexpectedly redirects to the homepage.
   */
  (function recoverHomepageRedirect() {
    try {
      const pending = sessionStorage.getItem(PENDING_KEY);
      if (pending !== "1") return;

      const currentUrl = new URL(window.location.href);

      if (
        currentUrl.pathname !== "/" ||
        currentUrl.searchParams.get("contact_posted") !== "true"
      ) {
        return;
      }

      const savedUrl = sessionStorage.getItem(RETURN_URL_KEY);
      if (!savedUrl) return;

      const productUrl = new URL(
        savedUrl,
        window.location.origin
      );

      if (
        productUrl.origin !== window.location.origin ||
        !/(^|\/)products\/[^/]+/.test(productUrl.pathname)
      ) {
        return;
      }

      productUrl.searchParams.set(
        "contact_posted",
        "true"
      );

      productUrl.hash = "contact_form";

      window.location.replace(productUrl.toString());

    } catch (error) {
      console.error(
        "Sold-out inquiry redirect recovery:",
        error
      );
    }
  })();

  function ensureModalInBody(modal) {
    if (!modal) return;
    if (modal.parentElement === document.body) return;

    const placeholder = document.createComment(
      "sold-out-inquiry-modal-placeholder"
    );

    modal._soldOutInquiryPlaceholder = placeholder;

    modal.parentNode.insertBefore(
      placeholder,
      modal
    );

    document.body.appendChild(modal);
  }

  function restoreModalToPlaceholder(modal) {
    if (!modal) return;

    const placeholder =
      modal._soldOutInquiryPlaceholder;

    if (placeholder && placeholder.parentNode) {
      placeholder.parentNode.insertBefore(
        modal,
        placeholder
      );

      placeholder.parentNode.removeChild(
        placeholder
      );
    }

    modal._soldOutInquiryPlaceholder = null;
  }

  function clearDraft() {
    try {
      sessionStorage.removeItem(DRAFT_KEY);
      sessionStorage.removeItem(PENDING_KEY);
      sessionStorage.removeItem(RETURN_URL_KEY);
      sessionStorage.removeItem(MODAL_ID_KEY);
    } catch (_) {}
  }

  function fixSwymInModal(modal) {
    if (!modal) return;

    modal
      .querySelectorAll(".swym-atw-button-container")
      .forEach((el) => el.remove());

    const submit = modal.querySelector(
      "[data-sold-out-inquiry-submit]"
    );

    const actions = modal.querySelector(
      ".sold-out-inquiry-modal__actions"
    );

    if (submit && actions && !actions.contains(submit)) {
      actions.appendChild(submit);
    }
  }

  function setModalOpen(modal) {
    if (!modal) return;

    modal.removeAttribute("hidden");
    modal.setAttribute("aria-hidden", "false");

    document.documentElement.classList.add(
      "sold-out-inquiry-modal-open"
    );

    document.body.classList.add(
      "sold-out-inquiry-modal-open"
    );
  }

  function setModalClosed(modal) {
    if (!modal) return;

    modal.setAttribute("hidden", "hidden");
    modal.setAttribute("aria-hidden", "true");

    document.documentElement.classList.remove(
      "sold-out-inquiry-modal-open"
    );

    document.body.classList.remove(
      "sold-out-inquiry-modal-open"
    );
  }

  function ensureCaptchaNote(modal) {
    if (!modal) return;

    let note = modal.querySelector(
      "[data-sold-out-inquiry-captcha-note]"
    );

    if (note) return;

    note = document.createElement("p");

    note.setAttribute(
      "data-sold-out-inquiry-captcha-note",
      ""
    );

    note.className =
      "sold-out-inquiry-modal__captcha-note";

    note.textContent =
      "If you get a CAPTCHA, your inquiry will be sent once it’s completed successfully.";

    const submitBtn = modal.querySelector(
      "[data-sold-out-inquiry-submit]"
    );

    if (submitBtn && submitBtn.parentNode) {
      submitBtn.parentNode.insertBefore(
        note,
        submitBtn.nextSibling
      );
    } else {
      modal.appendChild(note);
    }
  }

  function clearInlineErrors(modal) {
    if (!modal) return;

    modal
      .querySelectorAll("[data-sold-out-inquiry-error]")
      .forEach((el) => el.remove());
  }

  function showInlineMessage(modal, message) {
    if (!modal) return;

    let node = modal.querySelector(
      "[data-sold-out-inquiry-error]"
    );

    if (!node) {
      node = document.createElement("p");

      node.setAttribute(
        "data-sold-out-inquiry-error",
        ""
      );

      node.className =
        "sold-out-inquiry-modal__error";

      const submitBtn = modal.querySelector(
        "[data-sold-out-inquiry-submit]"
      );

      if (submitBtn && submitBtn.parentNode) {
        submitBtn.parentNode.insertBefore(
          node,
          submitBtn.nextSibling
        );
      } else {
        modal.appendChild(node);
      }
    }

    node.textContent = message;
  }

  function saveDraft(form) {
    try {
      const fd = new FormData(form);
      const saved = {};

      for (const [key, value] of fd.entries()) {
        if (typeof value === "string") {
          saved[key] = value;
        }
      }

      const modal = form.closest(
        ".sold-out-inquiry-modal"
      );

      if (modal && modal.id) {
        sessionStorage.setItem(
          MODAL_ID_KEY,
          modal.id
        );
      }

      sessionStorage.setItem(
        DRAFT_KEY,
        JSON.stringify(saved)
      );

      sessionStorage.setItem(PENDING_KEY, "1");

      sessionStorage.setItem(
        RETURN_URL_KEY,
        window.location.href
      );

    } catch (_) {}
  }

  function restoreDraftIntoModal(modal) {
    if (!modal) return;

    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      if (!raw) return;

      const draft = JSON.parse(raw);

      if (!draft || typeof draft !== "object") {
        return;
      }

      Object.keys(draft).forEach((name) => {
        const field = Array.from(
          modal.querySelectorAll("[name]")
        ).find((el) => el.name === name);

        if (field && "value" in field) {
          if (field.type === "hidden") return;

          field.value = draft[name];
        }
      });

    } catch (_) {}
  }

  function resetSubmitButton(modal) {
    if (!modal) return;

    const submitBtn = modal.querySelector(
      "[data-sold-out-inquiry-submit]"
    );

    if (!submitBtn) return;

    submitBtn.disabled = false;
    submitBtn.removeAttribute("aria-busy");
    submitBtn.textContent = "Submit Inquiry";
  }

  function openModal(modal) {
    if (!modal) return;

    ensureModalInBody(modal);
    setModalOpen(modal);
    clearInlineErrors(modal);
    ensureCaptchaNote(modal);
    fixSwymInModal(modal);

    let tries = 0;

    const interval = setInterval(() => {
      fixSwymInModal(modal);
      tries += 1;

      if (tries >= 10) {
        clearInterval(interval);
      }
    }, 100);
  }

  function closeModal(modal) {
    if (!modal) return;

    setModalClosed(modal);
    restoreModalToPlaceholder(modal);
  }

  function showSuccess(modal) {
    if (!modal) return;

    const form = modal.querySelector(
      "[data-sold-out-inquiry]"
    );

    if (form) {
      form.hidden = true;
      form.style.display = "none";
    }

    let successNode = modal.querySelector(
      "[data-sold-out-inquiry-success]"
    );

    if (!successNode) {
      successNode = document.createElement("div");

      successNode.setAttribute(
        "data-sold-out-inquiry-success",
        ""
      );

      successNode.className =
        "sold-out-inquiry-modal__success";

      const panel = modal.querySelector(
        ".sold-out-inquiry-modal__panel"
      );

      if (panel) {
        panel.appendChild(successNode);
      }
    }

    successNode.textContent =
      "Inquiry sent successfully. We’ll get back to you shortly.";
  }

  function resetModalForm(modal) {
    if (!modal) return;

    const form = modal.querySelector(
      "[data-sold-out-inquiry]"
    );

    const successNode = modal.querySelector(
      "[data-sold-out-inquiry-success]"
    );

    if (form) {
      form.hidden = false;
      form.style.display = "";

      // Clear customer-entered fields after success.
      // Hidden product information is preserved.
      form.reset();
    }

    if (successNode) {
      successNode.remove();
    }

    clearInlineErrors(modal);
    resetSubmitButton(modal);
  }

  function restoreAfterCaptcha() {
    try {
      const pending = sessionStorage.getItem(
        PENDING_KEY
      );

      if (pending !== "1") return;

      const modalId = sessionStorage.getItem(
        MODAL_ID_KEY
      );

      const modal = modalId
        ? document.getElementById(modalId)
        : document.querySelector(
            ".sold-out-inquiry-modal"
          );

      // Keep saved state if the modal is not available.
      if (!modal) return;

      const params = new URLSearchParams(
        window.location.search
      );

      const urlConfirmed =
        params.get("contact_posted") === "true";

      const liquidConfirmed = !!modal.querySelector(
        "[data-sold-out-inquiry-confirmed]"
      );

      const successConfirmed =
        urlConfirmed || liquidConfirmed;

      openModal(modal);

      if (successConfirmed) {
        showSuccess(modal);
        clearDraft();

        // Remove Shopify's success parameter
        // without reloading the page.
        if (urlConfirmed) {
          try {
            const url = new URL(
              window.location.href
            );

            url.searchParams.delete(
              "contact_posted"
            );

            window.history.replaceState(
              {},
              "",
              url.toString()
            );
          } catch (_) {}
        }

      } else {
        restoreDraftIntoModal(modal);

        showInlineMessage(
          modal,
          "Your inquiry has not been confirmed as sent. Please review your information and submit again if necessary."
        );

        try {
          sessionStorage.removeItem(PENDING_KEY);
          sessionStorage.removeItem(MODAL_ID_KEY);
        } catch (_) {}
      }

      resetSubmitButton(modal);

    } catch (error) {
      console.error(
        "Sold-out inquiry restoration error:",
        error
      );
    }
  }

  // Wait until the page HTML is available.
  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      restoreAfterCaptcha
    );
  } else {
    restoreAfterCaptcha();
  }

  // Handle modal opening and closing.
  document.addEventListener("click", function (e) {
    const openBtn = e.target.closest(
      "[data-sold-out-inquiry-open]"
    );

    if (openBtn) {
      e.preventDefault();

      const id = openBtn.getAttribute(
        "aria-controls"
      );

      const modal = id
        ? document.getElementById(id)
        : null;

      if (modal) {
        resetModalForm(modal);
        openModal(modal);
      }

      return;
    }

    const closeBtn = e.target.closest(
      "[data-sold-out-inquiry-close]"
    );

    if (closeBtn) {
      e.preventDefault();

      const modal = closeBtn.closest(
        ".sold-out-inquiry-modal"
      );

      closeModal(modal);
    }
  });

  // Escape key closes the modal.
  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape") return;

    document
      .querySelectorAll(
        ".sold-out-inquiry-modal:not([hidden])"
      )
      .forEach(closeModal);
  });

  // Shopify theme editor safety.
  document.addEventListener(
    "shopify:section:unload",
    function () {
      document
        .querySelectorAll(
          ".sold-out-inquiry-modal:not([hidden])"
        )
        .forEach(closeModal);
    }
  );

  // Handle the contact form submission.
  document.addEventListener("submit", function (e) {
    const form = e.target;

    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    const modal = form.closest(
      ".sold-out-inquiry-modal"
    );

    if (!modal) return;

    const submitBtn = form.querySelector(
      "[data-sold-out-inquiry-submit]"
    );

    if (!submitBtn) return;

    clearInlineErrors(modal);
    ensureCaptchaNote(modal);
    fixSwymInModal(modal);

    // Validate all required fields.
    if (!form.checkValidity()) {
      e.preventDefault();
      form.reportValidity();
      return;
    }

    // Save the customer input before navigation.
    saveDraft(form);

    // Prevent repeated submission clicks.
    submitBtn.disabled = true;
    submitBtn.setAttribute(
      "aria-busy",
      "true"
    );

    submitBtn.textContent = "Submitting…";

    // Allow Shopify's normal submission and CAPTCHA.
  });
})();
